import { createContext, PropsWithChildren, useCallback, useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { cloneDeep, isEqual } from "lodash-es";

import ThermostatIcon from "@mui/icons-material/Thermostat";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";

import { BeforeSaveFunction, BeforeSaveErrorType } from "../../BasicTemplate";
import { MeasRuleValue, ReportRule, Rule, RuleBase, RuleType, ThermowatchValue, Zone } from "./types";
import { useDevice } from "../../../../utils/DeviceProvider";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import ThermowatchDeviceSettings from "./ThermowatchDeviceSettings";
import ThermowatchReports from "./ThermowatchReports";
import ThermowatchAlarms from "./ThermowatchAlarms";
import ThermowatchZones from "./ThermowatchZones";
import { uuidv4 } from "../../../../utils/utils";
import { DeviceTemplate } from "../../../../utils/types";

type ThermowatchState = {
  value: ThermowatchValue;
  saved: ThermowatchValue | null;
  setValue: (value: ThermowatchValue) => void;
};
type ThermowatchContextType = ThermowatchState & {
  reports: Rule<ReportRule>[];
  alarms: Rule<MeasRuleValue[]>[];
};

const ThermowatchContext = createContext<ThermowatchContextType>({} as ThermowatchContextType);

export const ThermowatchProvider = ({ children }: PropsWithChildren<{}>) => {
  const {
    device: { template, currentValue, config, noConfig },
    setCurrentValue,
    setEmptyConfig,
  } = useDevice();

  const setValue = useCallback(
    (value: ThermowatchValue) => {
      setCurrentValue((old: any) => {
        if (!old) {
          return old;
        }
        old.thermowatch = value;
        return { ...old };
      });
    },
    [setCurrentValue]
  );

  useEffect(() => {
    if (!template?.template || !Object.keys(template.template).includes("thermowatch")) {
      return;
    }
    if ((noConfig || (currentValue && !currentValue.thermowatch)) && template) {
      const config = emptyConfig(template.template);
      setEmptyConfig(config, "thermowatch");
      setCurrentValue((old: any) => ({ ...old, thermowatch: config }));
      // } else if (currentValue?.thermowatch) {
      //   // TODO: átálláshoz;
      //   let changed = false;
      //   (currentValue.thermowatch as ThermowatchValue).report.forEach((report) => {
      //     if (!report.uuid) {
      //       report.uuid = uuidv4();
      //       changed = true;
      //     }
      //   });
      //   (currentValue.thermowatch as ThermowatchValue).alarm.forEach((alarm) => {
      //     if (!alarm.uuid) {
      //       alarm.uuid = uuidv4();
      //       changed = true;
      //     }
      //   });
      //   (currentValue.thermowatch as ThermowatchValue).zones.forEach((zone, index) => {
      //     const z = typeof zone === "string" ? ({ address: zone as unknown as string } as Zone) : zone;
      //     if (!z.uuid) {
      //       z.uuid = uuidv4();
      //       changed = true;
      //     }
      //     if (z.report !== undefined && typeof z.report === "number") {
      //       z.report = z.report === -1 ? "-1" : (currentValue.thermowatch as ThermowatchValue).report[z.report].uuid;
      //       changed = true;
      //     }
      //     if (z.alarm !== undefined && typeof z.alarm === "number") {
      //       z.alarm = z.alarm === -1 ? "-1" : (currentValue.thermowatch as ThermowatchValue).alarm[z.alarm].uuid;
      //       changed = true;
      //     }
      //     (currentValue.thermowatch as ThermowatchValue).zones[index] = z;
      //   });
      //   if (changed) {
      //     console.log("módosítok.");
      //     setCurrentValue(currentValue);
      //     setEmptyConfig(currentValue.thermowatch, "thermowatch");
      //   }
    }
  }, [template, currentValue, noConfig, setCurrentValue, setEmptyConfig]);

  if (!template?.template || !Object.keys(template.template).includes("thermowatch")) {
    return <>{children}</>;
  }

  return (
    <ThermowatchContext.Provider
      value={{
        value: cloneDeep(currentValue?.thermowatch) as ThermowatchValue,
        saved: config?.toolboxConfig ? (config.toolboxConfig.thermowatch as ThermowatchValue) : null,
        setValue,
        reports: currentValue ? (currentValue.thermowatch as ThermowatchValue)?.report : ([] as Rule<ReportRule>[]),
        alarms: currentValue ? (currentValue.thermowatch as ThermowatchValue)?.alarm : ([] as Rule<MeasRuleValue[]>[]),
      }}
    >
      {children}
    </ThermowatchContext.Provider>
  );
};

export const useThermowatch = () => {
  const thermowatchState = useContext(ThermowatchContext);
  if (thermowatchState === null) {
    throw new Error("useThermowatch must be used within an ThermowatchProvider");
  }

  return thermowatchState;
};

export const useBeforeSave = () => {
  // const { formatMessage } = useIntl();

  const beforeSave: BeforeSaveFunction<ThermowatchValue> = (value) => {
    const processed = cloneDeep(value);
    if (!processed) {
      return { processed };
    }
    // deleted items
    const deletedReports = processed.report.filter((report) => report.deleted).map((report) => report.uuid);
    const deletedAlarms = processed.alarm.filter((partition) => partition.deleted).map((alarm) => alarm.uuid);

    processed.zones.forEach((zone) => {
      if (zone.deleted) {
        return;
      }
      if (zone.report && deletedReports.includes(zone.report)) {
        zone.report = "-1";
      }
      if (zone.alarm && deletedAlarms.includes(zone.alarm)) {
        zone.alarm = "-1";
      }
    });

    // delete
    for (let i = processed.report.length - 1; i >= 0; i--) {
      if (processed.report[i].deleted) {
        processed.report.splice(i, 1);
      }
    }
    for (let i = processed.alarm.length - 1; i >= 0; i--) {
      if (processed.alarm[i].deleted) {
        processed.alarm.splice(i, 1);
      }
    }
    for (let i = processed.zones.length - 1; i >= 0; i--) {
      if (processed.zones[i].deleted) {
        processed.zones.splice(i, 1);
      }
    }

    const errors: BeforeSaveErrorType[] = [];
    // check something

    return { processed, errors: errors.length > 0 ? errors : undefined };
  };

  return beforeSave;
};

export const useThermowatchSubPages = () => {
  // const menuStyles = useMenuStyles();
  const editedStyle = useEditedStyles();
  const { deviceSettingsEdited, reportsEdited, alarmsEdited, zonesEdited } = useEdited();

  return {
    sidebarOption: {
      link: "/thermowatch-device",
      name: "Thermowatch",
      icon: <ThermostatIcon />,
      classname: deviceSettingsEdited || reportsEdited || alarmsEdited || zonesEdited ? editedStyle.menuItem : "",
      subPages: [
        {
          link: "/thermowatch-device",
          name: <FormattedMessage defaultMessage="Device settings" />,
          icon: <DesignServicesIcon />,
          classname: deviceSettingsEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/thermowatch-reports",
          name: <FormattedMessage defaultMessage="Reports" />,
          icon: <SmsOutlinedIcon />,
          classname: reportsEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/thermowatch-alarms",
          name: <FormattedMessage defaultMessage="Alarms" />,
          icon: <FeedbackOutlinedIcon />,
          classname: alarmsEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/thermowatch-zones",
          name: <FormattedMessage defaultMessage="Zones" />,
          icon: <GridViewOutlinedIcon />,
          classname: zonesEdited ? editedStyle.menuItem : "",
        },
      ],
    },
    children: [
      { link: "/thermowatch-device", children: <ThermowatchDeviceSettings /> },
      { link: "/thermowatch-reports", children: <ThermowatchReports /> },
      { link: "/thermowatch-alarms", children: <ThermowatchAlarms /> },
      { link: "/thermowatch-zones", children: <ThermowatchZones /> },
    ],
  };
};

const useEdited = () => {
  const { value, saved } = useThermowatch();
  const beforeSave = useBeforeSave();

  const { processed } = value ? beforeSave(value) : { processed: value };

  const battery = value ? !isEqual(processed?.battery, saved?.battery) : false;

  return {
    deviceSettingsEdited: battery,
    reportsEdited: value ? !isEqual(processed?.report, saved?.report) : false,
    alarmsEdited: value ? !isEqual(processed?.alarm, saved?.alarm) : false,
    zonesEdited: value ? !isEqual(processed?.zones, saved?.zones) : false,
  };
};

export const useRuleTypes = (): RuleBase<RuleType> => {
  const { formatMessage } = useIntl();
  return {
    temperature: {
      label: formatMessage({ defaultMessage: "Temperature" }),
      measUnit: formatMessage({ defaultMessage: "°C" }),
      icon: <ThermostatIcon />,
    },
    humidity: {
      label: formatMessage({ defaultMessage: "Humidity" }),
      measUnit: formatMessage({ defaultMessage: "%" }),
      icon: <CloudQueueIcon />,
      inputProps: {
        min: 0,
        max: 100,
      },
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emptyConfig = (template: DeviceTemplate): ThermowatchValue => {
  return {
    battery: {
      delay: 3600,
      e: "216A",
      hyst: 50,
      warn: {
        v: 2800,
        e: "1808",
        r: "3808",
      },
      alarm: {
        v: 2500,
        e: "1302",
        r: "3302",
      },
    },
    rssi: {
      delay: 3600,
      e: "217A",
    },
    comm: {
      warn: {
        v: 300,
        e: "1B50",
        r: "3B50",
      },
      alarm: {
        v: 3600,
        e: "1350",
        r: "3350",
      },
    },
    report: [] as Rule<ReportRule>[],
    alarm: [] as Rule<MeasRuleValue[]>[],
    zones: [] as Zone[],
  };
};

export const loadConfig = (old: ThermowatchValue, template: DeviceTemplate, ruleTypes: RuleBase<RuleType>) => {
  const config = emptyConfig(template);
  config.battery = { ...config.battery, ...old?.battery };
  config.rssi = { ...config.rssi, ...old?.rssi };
  config.comm = { ...config.comm, ...old?.comm };
  config.report =
    old?.report?.map((r: Rule<ReportRule>) => {
      const { uuid, name, ...other } = r;
      const report = { uuid: uuid || uuidv4(), name } as Rule<ReportRule>;
      (Object.keys(ruleTypes) as Array<keyof RuleBase<RuleType>>).forEach((key) => {
        report[key] = other[key] as ReportRule;
      });
      return report;
    }) || [];
  config.alarm =
    old?.alarm?.map((a: Rule<MeasRuleValue[]>) => {
      const { uuid, name, ...other } = a;
      const alarm = { uuid: uuid || uuidv4(), name } as Rule<MeasRuleValue[]>;
      (Object.keys(ruleTypes) as Array<keyof RuleBase<RuleType>>).forEach((key) => {
        alarm[key] = other[key] as MeasRuleValue[];
      });
      return alarm;
    }) || [];
  return config;
};
